import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { ArrowLeft, Loader2, Eye, EyeOff, CheckCircle2 } from "lucide-react";
import AuthShell from "../components/AuthShell";

function apiError(e) {
  const d = e?.response?.data?.detail;
  if (!d) return "Une erreur est survenue.";
  if (typeof d === "string") return d;
  return JSON.stringify(d);
}

export default function ResetPassword() {
  const [search] = useSearchParams();
  const token = search.get("token") || "";
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw.length < 8) { setErr("Mot de passe : 8 caractères minimum."); return; }
    if (pw !== confirm) { setErr("Les mots de passe ne correspondent pas."); return; }
    setLoading(true);
    try {
      await api.authReset(token, pw);
      setDone(true);
      setTimeout(() => nav("/connexion", { replace: true }), 1800);
    } catch (ex) { setErr(apiError(ex)); }
    finally { setLoading(false); }
  };

  return (
    <AuthShell dataTestId="reset-page" title="Nouveau mot de passe" subtitle="Choisissez un mot de passe fort — vous en aurez besoin à chaque connexion.">
      {!token && (
        <div className="rounded-md bg-[#FDECEC] border border-[#F5C7C7] p-3 text-[13px] text-[#7C2A21] mb-4">Lien invalide — jeton manquant.</div>
      )}
      {done ? (
        <div className="rounded-md bg-[#E4F4EA] border border-[#C0E1CC] p-4 text-[13.5px] text-[#1B5C39]" data-testid="reset-done">
          <CheckCircle2 className="w-4 h-4 inline mr-1" /> Mot de passe mis à jour. Redirection vers la connexion…
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-4" data-testid="reset-form">
          <div>
            <label className="block text-[12px] font-semibold text-[#111111] mb-1.5">Nouveau mot de passe</label>
            <div className="relative">
              <input type={showPw ? "text" : "password"} minLength={8} required value={pw} onChange={(e) => setPw(e.target.value)}
                data-testid="reset-pw" className="w-full h-11 pl-3 pr-10 rounded-md border border-[#CDD7E3] bg-white text-[14px] focus:outline-none focus:ring-2 focus:ring-[#2878FF]/25 focus:border-[#2878FF]" />
              <button type="button" onClick={() => setShowPw((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#5E6875]">
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-[12px] font-semibold text-[#111111] mb-1.5">Confirmer</label>
            <input type={showPw ? "text" : "password"} minLength={8} required value={confirm} onChange={(e) => setConfirm(e.target.value)}
              data-testid="reset-confirm" className="w-full h-11 px-3 rounded-md border border-[#CDD7E3] bg-white text-[14px] focus:outline-none focus:ring-2 focus:ring-[#2878FF]/25 focus:border-[#2878FF]" />
          </div>
          {err && <div className="text-[12.5px] text-[#7C2A21] bg-[#FDECEC] border border-[#F5C7C7] rounded-md px-3 py-2" data-testid="reset-error">{err}</div>}
          <button type="submit" disabled={loading || !token} data-testid="reset-submit"
            className="w-full h-11 rounded-md text-[#111111] text-[14px] font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60"
            style={{ background: "#FF9F1C" }}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            Mettre à jour
          </button>
        </form>
      )}
      <Link to="/connexion" className="mt-6 inline-flex items-center gap-1 text-[13px] text-[#1769E0] hover:text-[#0B1F33] font-medium">
        <ArrowLeft className="w-3.5 h-3.5" /> Retour à la connexion
      </Link>
    </AuthShell>
  );
}
